import { forwardRef, useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { AlertTriangle, CheckCircle2, Info, Loader2, X, XCircle } from 'lucide-react';

export function cx(...c: (string | false | null | undefined)[]): string {
  return c.filter(Boolean).join(' ');
}

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'gold' | 'coral' | 'white';
const variants: Record<Variant, string> = {
  primary: 'bg-forest-700 text-white shadow-sm hover:bg-forest-800 disabled:bg-forest-200',
  secondary: 'bg-white text-ink border border-line hover:bg-ivory disabled:text-muted',
  ghost: 'bg-transparent text-forest-700 hover:bg-forest-50 disabled:text-muted',
  danger: 'bg-white text-danger border border-danger/30 hover:bg-danger-soft',
  gold: 'bg-gold-soft text-ink border border-gold/50 hover:bg-sun-50',
  coral: 'bg-coral-500 text-ink shadow-sm hover:bg-coral-600 hover:text-white',
  white: 'bg-white/95 text-ink shadow-sm hover:bg-white',
};

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'md' | 'sm' | 'lg'; busy?: boolean; icon?: ReactNode }>(
  function Button({ variant = 'primary', size = 'md', busy, icon, className, children, disabled, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type={rest.type ?? 'button'}
        disabled={disabled || busy}
        aria-busy={busy || undefined}
        className={cx(
          'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold transition-all active:scale-[0.98] disabled:cursor-not-allowed',
          size === 'sm' && 'min-h-9 px-3 text-sm',
          size === 'md' && 'min-h-11 px-5 text-[0.9375rem]',
          size === 'lg' && 'min-h-12 px-6 text-base',
          variants[variant],
          className,
        )}
        {...rest}
      >
        {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
        {children}
      </button>
    );
  },
);

export function IconButton({ label, children, className, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button type="button" aria-label={label} title={label} className={cx('inline-flex size-11 items-center justify-center rounded-xl text-muted hover:bg-ivory hover:text-ink', className)} {...rest}>
      {children}
    </button>
  );
}

export function Card({ className, children, as: As = 'section', ...rest }: { className?: string; children: ReactNode; as?: 'section' | 'div' | 'article' | 'li' } & Record<string, unknown>) {
  return <As className={cx('card', className)} {...rest}>{children}</As>;
}

type Tone = 'neutral' | 'forest' | 'gold' | 'danger' | 'warn' | 'info' | 'coral' | 'ocean' | 'turquoise';
const tones: Record<Tone, string> = {
  neutral: 'bg-ivory text-muted border-line',
  forest: 'bg-ok-50 text-ok-700 border-ok-100',
  gold: 'bg-gold-soft text-gold-ink border-gold/30',
  danger: 'bg-danger-soft text-danger border-danger/20',
  warn: 'bg-warn-soft text-warn border-warn/20',
  info: 'bg-info-soft text-info border-info/20',
  coral: 'bg-coral-50 text-coral-700 border-coral-100',
  ocean: 'bg-ocean-50 text-ocean-700 border-ocean-100',
  turquoise: 'bg-turquoise-50 text-turquoise-700 border-turquoise-500/30',
};

export function Badge({ tone = 'neutral', children, icon, className }: { tone?: Tone; children: ReactNode; icon?: ReactNode; className?: string }) {
  return <span className={cx('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap', tones[tone], className)}>{icon}{children}</span>;
}

/** Severity is always conveyed with an icon and words, never colour alone. */
export function SeverityBadge({ severity }: { severity: 'high' | 'medium' | 'low' }) {
  if (severity === 'high') return <Badge tone="danger" icon={<XCircle className="size-3.5" aria-hidden />}>Urgent</Badge>;
  if (severity === 'medium') return <Badge tone="warn" icon={<AlertTriangle className="size-3.5" aria-hidden />}>Check</Badge>;
  return <Badge tone="info" icon={<Info className="size-3.5" aria-hidden />}>FYI</Badge>;
}

export function Notice({ tone = 'info', title, children, action, className }: { tone?: 'info' | 'warn' | 'danger' | 'success' | 'neutral'; title?: ReactNode; children?: ReactNode; action?: ReactNode; className?: string }) {
  const map = {
    info: ['bg-info-soft border-info/20 text-info', Info],
    warn: ['bg-warn-soft border-warn/20 text-warn', AlertTriangle],
    danger: ['bg-danger-soft border-danger/20 text-danger', XCircle],
    success: ['bg-ok-50 border-ok-100 text-ok-700', CheckCircle2],
    neutral: ['bg-white border-line text-muted', Info],
  } as const;
  const [cls, Icon] = map[tone];
  return (
    <div className={cx('flex gap-3 rounded-xl border p-3 text-sm', cls, className)} role={tone === 'danger' ? 'alert' : undefined}>
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1 text-ink">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={cx(title ? 'mt-0.5' : '', 'text-ink/80 break-anywhere')}>{children}</div>}
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}

export function Field({ label, hint, error, children, id }: { label: ReactNode; hint?: ReactNode; error?: string | null; children: (id: string, describedBy?: string) => ReactNode; id?: string }) {
  const auto = useId();
  const fid = id ?? auto;
  const hintId = hint ? `${fid}-hint` : undefined;
  const errId = error ? `${fid}-err` : undefined;
  return (
    <div>
      <label htmlFor={fid} className="field-label">{label}</label>
      {children(fid, [hintId, errId].filter(Boolean).join(' ') || undefined)}
      {hint && <p id={hintId} className="field-hint">{hint}</p>}
      {error && <p id={errId} className="mt-1 text-sm text-danger" role="alert">{error}</p>}
    </div>
  );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...rest }, ref) {
  return <input ref={ref} className={cx('input', className)} {...rest} />;
});
export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cx('input pr-8', className)} {...rest}>{children}</select>;
}
export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx('input min-h-24', className)} {...rest} />;
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-muted" role="status">
      <Loader2 className="size-5 animate-spin" aria-hidden />
      <span>{label}…</span>
    </div>
  );
}

export function EmptyState({ icon, title, children, actions }: { icon?: ReactNode; title: string; children?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-4 py-10 text-center">
      {icon && <div className="mb-3 flex size-12 items-center justify-center rounded-2xl bg-forest-50 text-forest-700">{icon}</div>}
      <h3 className="font-display text-lg">{title}</h3>
      {children && <div className="mt-1 max-w-md text-sm text-muted">{children}</div>}
      {actions && <div className="mt-4 flex flex-wrap justify-center gap-2">{actions}</div>}
    </div>
  );
}

/** Accessible modal sheet built on <dialog>: focus trap, Esc to close, bottom sheet on phones. */
export function Sheet({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const openRef = useRef(open);
  openRef.current = open;
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      // Only report closes the user caused; a programmatic close (open → false) must not
      // call onClose, or handing off from one sheet to another would cancel the second.
      onClose={() => { if (openRef.current) onClose(); }}
      onCancel={(e) => { e.preventDefault(); onClose(); }}
      aria-labelledby={titleId}
      className={cx(
        'm-0 mt-auto w-full max-w-none rounded-t-3xl border border-line bg-white p-0 text-ink shadow-xl backdrop:bg-ink/50 backdrop:backdrop-blur-sm',
        'sm:m-auto sm:rounded-3xl', wide ? 'sm:max-w-2xl' : 'sm:max-w-lg',
      )}
      style={{ maxHeight: '92dvh' }}
    >
      {open && (
        <div className="flex max-h-[92dvh] flex-col">
          <div className="flex items-center justify-between gap-2 border-b border-line px-5 py-3">
            <h2 id={titleId} className="font-display text-lg">{title}</h2>
            <IconButton label="Close" onClick={onClose}><X className="size-5" /></IconButton>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="safe-bottom flex flex-wrap justify-end gap-2 border-t border-line px-5 py-3">{footer}</div>}
        </div>
      )}
    </dialog>
  );
}

export function Tabs<T extends string>({ value, onChange, items, label, className }: { value: T; onChange: (v: T) => void; items: { value: T; label: ReactNode; count?: number }[]; label: string; className?: string }) {
  return (
    <div role="tablist" aria-label={label} className={cx('scroll-row flex min-w-0 max-w-full gap-1 overflow-x-auto rounded-full border border-line bg-white p-1 pb-1', className)}>
      {items.map((it) => (
        <button
          key={it.value}
          role="tab"
          type="button"
          aria-selected={value === it.value}
          onClick={() => onChange(it.value)}
          className={cx('min-h-9 shrink-0 whitespace-nowrap rounded-full px-4 text-sm font-semibold transition-colors', value === it.value ? 'bg-ink text-white' : 'text-muted hover:bg-ivory hover:text-ink')}
        >
          {it.label}
          {it.count !== undefined && <span className={cx('ml-1.5 text-xs', value === it.value ? 'text-white/70' : 'text-muted')}>{it.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function Avatar({ name, color = 'forest', size = 'md', className }: { name: string; color?: string; size?: 'sm' | 'md' | 'lg'; className?: string }) {
  const bg: Record<string, string> = { forest: 'bg-forest-100 text-forest-800', gold: 'bg-sun-500 text-ink', clay: 'bg-coral-100 text-coral-700', sea: 'bg-ocean-100 text-ocean-700', plum: 'bg-[#efe1ee] text-[#62305e]', stone: 'bg-ivory-dark text-muted' };
  const initials = name.replace(/\([^)]*\)/g, ' ').split(/\s+/).filter((w) => /^\p{L}/u.test(w)).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('') || '?';
  return <span aria-hidden className={cx('inline-flex shrink-0 items-center justify-center rounded-full font-medium', bg[color] ?? bg.forest, size === 'sm' ? 'size-7 text-xs' : size === 'lg' ? 'size-14 text-lg' : 'size-9 text-sm', className)}>{initials}</span>;
}

export function SectionHeader({ title, action, id, children }: { title: ReactNode; action?: ReactNode; id?: string; children?: ReactNode }) {
  return (
    <div className="mb-3 flex flex-wrap items-end justify-between gap-x-3 gap-y-2">
      <div className="min-w-0 flex-[1_1_12rem]">
        <h2 id={id} className="font-display text-xl">{title}</h2>
        {children && <p className="mt-0.5 text-sm text-muted">{children}</p>}
      </div>
      {action}
    </div>
  );
}
